import { Link, useLocation } from 'react-router-dom';
import { Search, Clock, Star, User, CreditCard, Settings, LogOut, Menu, X, Key, Shield, Globe, Store, FileText, Users, Activity, Compass, Bell, Swords, Database, Handshake, Gavel } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '../../stores/authStore';
import DarkModeToggle from '../DarkModeToggle';

const navigation = [
  { name: 'LinkDiry', href: '/dashboard', icon: Search },
  { name: 'Discover', href: '/discover', icon: Compass },
  { name: 'Competitor Analysis', href: '/competitor', icon: Swords },
  { name: 'Campaigns', href: '/campaigns', icon: Gavel },
  { name: 'Price Alerts', href: '/alerts', icon: Bell },
  { name: 'History', href: '/history', icon: Clock },
  { name: 'Saved Lists', href: '/saved', icon: Star },
  { name: 'Profile', href: '/profile', icon: User },
  { name: 'Billing', href: '/billing', icon: CreditCard },
  { name: 'API Keys', href: '/api-keys', icon: Key },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const adminNavigation = [
  { name: 'Admin Overview', href: '/admin', icon: Shield },
  { name: 'Domains', href: '/admin/domains', icon: Globe },
  { name: 'Sellers', href: '/admin/sellers', icon: Store },
  { name: 'Submissions', href: '/admin/submissions', icon: FileText },
  { name: 'Link Monitor', href: '/admin/monitor', icon: Activity },
  { name: 'Data Scraping', href: '/admin/scraping', icon: Database },
  { name: 'Users', href: '/admin/users', icon: Users },
  { name: 'Partners', href: '/admin/partners', icon: Handshake },
];

export default function Sidebar() {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const navItems = user?.is_admin
    ? [...navigation, ...adminNavigation]
    : navigation;

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={toggleMobileMenu}
          className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow-lg"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-700 dark:text-gray-300" />
          ) : (
            <Menu className="h-6 w-6 text-gray-700 dark:text-gray-300" />
          )}
        </button>
      </div>

      {/* Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleMobileMenu}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col z-40 transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-800">
          <Link to="/dashboard" className="flex items-center gap-2.5 group">
            <div className="w-9 h-9 bg-gradient-to-br from-brand-500 to-brand-400 rounded-lg flex items-center justify-center shadow-sm shadow-brand-500/25 flex-shrink-0">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="10" cy="9" r="4.7" fill="white"/>
                <circle cx="10" cy="9" r="2.4" fill="#FBBF24"/>
                <rect x="12.4" y="11.6" width="2.4" height="4.4" rx="1.1" transform="rotate(-30 13.6 13.8)" fill="white"/>
                <rect x="3" y="16" width="18" height="2.8" rx="1.4" fill="white"/>
              </svg>
            </div>
            <span className="text-xl font-bold">
              <span className="text-slate-900 dark:text-white">Link</span>
              <span className="text-brand-500">Diry</span>
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
            const isAdmin = adminNavigation.some(n => n.href === item.href);
            return (
              <div key={item.name}>
                {isAdmin && (
                  <div className="mt-4 mb-2 px-4">
                    <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">Admin</p>
                  </div>
                )}
                <Link
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium text-sm">{item.name}</span>
                </Link>
              </div>
            );
          })}
        </nav>

        {/* User info and logout */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-800 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {user?.full_name || user?.email}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                {(user?.plan || 'free').charAt(0).toUpperCase() + (user?.plan || 'free').slice(1)} Plan
              </p>
            </div>
            <div className="flex items-center gap-0.5">
              <DarkModeToggle />
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-3 px-4 py-2.5 rounded-lg w-full text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium text-sm">Logout</span>
          </button>
        </div>
      </aside>
    </>
  );
}
